import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Typography,
  ModalFooter,
  Button,
} from '@one-thd/sui-atomic-components';
import '../../../styles/returns.scss';
import { TagOnCopyTextToClipboard } from '../../../utils/analytics-tagging';
import { ReturnFraudType } from '../../../constants/analytics';

interface ICopyState {
  color: string;
  text: string;
}

interface IFraudModalProps {
  title: string;
  body: string;
  buttonText: string;
  onClick: () => void;
  returnFraudType?: ReturnFraudType;
}

const FraudModal = ({
  title,
  body,
  buttonText,
  onClick,
  returnFraudType,
}: IFraudModalProps) => {
  const [copyState, setCopyState] = useState<ICopyState>();

  // Use regular expressions to remove <b>,</b>, and <br> tags
  const cleanBodyMessage = (inputString = '') =>
    inputString.replace(/<b>|<\/b>|<br>/g, '');

  const copyMessage = () => {
    // Tag the event when the user copies the message to the clipboard
    TagOnCopyTextToClipboard(returnFraudType);
    navigator.clipboard
      .writeText(cleanBodyMessage(body))
      .then(() => {
        setCopyState(getCopyMessage('SUCCESS'));
      })
      .catch(() => {
        setCopyState(getCopyMessage('ERROR'));
      });
  };
  const getCopyMessage = (type: string) => () => {
    return {
      SUCCESS: {
        color: 'success',
        text: 'Copied to Clipboard',
      },
      ERROR: {
        color: 'danger',
        text: 'Something went wrong. Please try again.',
      },
    }[type];
  };

  return (
    <Modal open onClose={onClick} size="sm" data-testid="fraud-modal">
      <ModalHeader onClose={onClick}>{title}</ModalHeader>
      <ModalBody>
        <div className="sui-pb-2 sui-font-regular sui-text-base sui-tracking-normal sui-normal-case sui-line-clamp-unset">
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }}
          />
        </div>
        <div className="sui-pb-2">
          <Button className="sui-underline" onClick={copyMessage}>
            <Typography variant="body-base">Copy Message</Typography>
          </Button>
        </div>
        {copyState && (
          <div className="sui-pb-2">
            <Typography color={copyState.color} variant="body-base">
              {copyState.text}
            </Typography>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={onClick}
          fullWidth
          data-testid="modal-action"
        >
          {buttonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FraudModal;
